/**
 * Turning "which company is this person looking at" into an answer, or into a
 * specific refusal.
 *
 * Pure, because the refusals matter as much as the answer and each one needs a
 * test. In particular: somebody who is not a member must be told they do not
 * have access — not shown a 404 that confirms the company exists, and not
 * silently redirected into a company they did not ask for.
 */
import type { Capability, MemberId, RoleId, UserId, WorkspaceId } from '@timely/domain';
import { asMemberId, asRoleId, asUserId, asWorkspaceId } from '@timely/domain';
import type { TenantContext, WorkspaceMembership, WorkspaceSummary } from '@timely/domain';

/** One row of "companies this person belongs to", as the database returns it. */
export interface MembershipRow {
    member_id: string;
    workspace_id: string;
    user_id: string;
    role_id: string;
    role_name: string;
    is_owner: boolean;
    capabilities: string[] | null;
    display_name: string | null;
    code: string | null;
    status: string;
    workspace: {
        id: string;
        name: string;
        slug: string;
        logo_path: string | null;
        currency: string;
        locale: string;
        timezone: string;
        phone_country: string;
    };
}

export function toTenantContext(row: MembershipRow): TenantContext {
    const workspace: WorkspaceSummary = {
        id: asWorkspaceId(row.workspace.id),
        name: row.workspace.name,
        slug: row.workspace.slug,
        ...(row.workspace.logo_path ? { logoPath: row.workspace.logo_path } : {}),
        currency: row.workspace.currency,
        locale: row.workspace.locale,
        timezone: row.workspace.timezone,
        phoneCountry: row.workspace.phone_country,
    };
    const membership: WorkspaceMembership = {
        memberId: asMemberId(row.member_id) as MemberId,
        workspaceId: asWorkspaceId(row.workspace_id) as WorkspaceId,
        userId: asUserId(row.user_id) as UserId,
        roleId: asRoleId(row.role_id) as RoleId,
        roleName: row.role_name,
        isOwner: row.is_owner,
        displayName: row.display_name ?? '',
        ...(row.code ? { code: row.code } : {}),
        // The owner role is not listed out; `holds()` lets it through anyway.
        capabilities: (row.capabilities ?? []) as Capability[],
        status: (row.status as WorkspaceMembership['status']) ?? 'active',
    };
    return { workspace, membership };
}

export type WorkspaceResolution =
    /** Signed in, verified, and belongs to nothing yet: make them a company. */
    | { status: 'no-workspaces' }
    /** No slug asked for, and exactly one to choose: go there. */
    | { status: 'redirect'; slug: string }
    /** No slug asked for, several to choose from: ask. */
    | { status: 'choose'; contexts: TenantContext[] }
    /** Asked for a company they are in. */
    | { status: 'ok'; context: TenantContext }
    /**
     * Asked for a company they are not in — or one that does not exist, which
     * is deliberately the same answer. Saying "no such company" would confirm
     * which slugs are real, and a slug is a company's name.
     */
    | { status: 'forbidden' };

export function resolveWorkspace(
    contexts: TenantContext[],
    slug: string | null | undefined,
    lastUsedSlug?: string | null,
): WorkspaceResolution {
    const active = contexts.filter(c => c.membership.status === 'active');
    if (active.length === 0) return { status: 'no-workspaces' };

    if (slug) {
        const found = active.find(c => c.workspace.slug === slug);
        return found ? { status: 'ok', context: found } : { status: 'forbidden' };
    }

    if (active.length === 1) return { status: 'redirect', slug: active[0]!.workspace.slug };

    const remembered = active.find(c => c.workspace.slug === lastUsedSlug);
    if (remembered) return { status: 'redirect', slug: remembered.workspace.slug };

    return { status: 'choose', contexts: active };
}

/**
 * Whether the company switcher should exist at all.
 *
 * One company means no switcher and nothing hinting that companies are a thing
 * you can have several of (owner decision D2).
 */
export const shouldShowSwitcher = (contexts: TenantContext[]): boolean =>
    contexts.filter(c => c.membership.status === 'active').length > 1;
